import { CardShapeLayout } from "./cardShapeLayout"

function arc(rx: number, ry: number, sweep: 0 | 1, x: number, y: number): string {
  return `A ${rx} ${ry} 0 0 ${sweep} ${x} ${y}`
}

interface StepMetrics {
  sx: number
  hx: number
  r: number
  ir: number
}

function computeStepMetrics(
  width: number,
  radius: number,
  innerRadius: number,
  yLo: number,
  yHi: number,
  stepX: number,
  edgeSpan: number
): StepMetrics {
  const sx = Math.max(radius + innerRadius, Math.min(stepX, width - radius - innerRadius))
  const hx = width - sx
  const r = Math.min(radius, width / 2, edgeSpan * 0.92, sx * 0.55)
  const ir = Math.min(
    innerRadius,
    sx * 0.48,
    Math.max(4, yLo - yHi) * 0.45,
    width * 0.1
  )
  return { sx, hx, r, ir }
}

/**
 * Top — нижний край Z (справа налево):
 * горизонталь yHi → вертикаль в sx → горизонталь yLo.
 */
function stepCutBottomFromRight(
  width: number,
  { sx, r, ir }: StepMetrics,
  yLo: number,
  yHi: number
): string[] {
  const yTop = Math.max(r + 1, yHi)

  return [
    `V ${Math.max(r, yTop - r)}`,
    arc(r, r, 1, width - r, yTop),
    `H ${sx + ir}`,
    arc(ir, ir, 0, sx, yTop + ir),
    `V ${yLo - ir}`,
    arc(ir, ir, 1, sx - ir, yLo),
    `H ${r}`,
    arc(r, r, 1, 0, Math.max(r, yLo - r)),
  ]
}

/**
 * Bottom — верхний край Z (зеркало, справа налево):
 * горизонталь yHi → вертикаль в hx → горизонталь yLo.
 */
function stepCutTopFromRight(
  _width: number,
  { hx, r, ir }: StepMetrics,
  yLo: number,
  yHi: number
): string[] {
  return [
    `H ${hx + ir}`,
    arc(ir, ir, 0, hx, yHi + ir),
    `V ${yLo - ir}`,
    arc(ir, ir, 1, hx - ir, yLo),
    `H ${r}`,
  ]
}

export function buildTopBannerPath(
  width: number,
  radius: number,
  innerRadius: number,
  bottomLeftY: number,
  bottomRightY: number,
  stepX: number
): string {
  const yLo = bottomLeftY
  const yHi = bottomRightY
  const metrics = computeStepMetrics(width, radius, innerRadius, yLo, yHi, stepX, yHi)
  const { r } = metrics

  return [
    `M ${r} 0`,
    `H ${width - r}`,
    arc(r, r, 1, width, r),
    ...stepCutBottomFromRight(width, metrics, yLo, yHi),
    `V ${r}`,
    arc(r, r, 1, r, 0),
    "Z",
  ].join(" ")
}

export function buildBottomBannerPath(
  width: number,
  height: number,
  radius: number,
  innerRadius: number,
  topLeftY: number,
  topRightY: number,
  stepX: number,
  outerEdgeSpan: number
): string {
  const yLo = topLeftY
  const yHi = topRightY
  const metrics = computeStepMetrics(
    width,
    radius,
    innerRadius,
    yLo,
    yHi,
    stepX,
    outerEdgeSpan
  )
  const { r } = metrics

  return [
    `M ${r} ${height}`,
    `H ${width - r}`,
    arc(r, r, 0, width, height - r),
    `V ${r}`,
    arc(r, r, 0, width - r, yHi),
    ...stepCutTopFromRight(width, metrics, yLo, yHi),
    arc(r, r, 0, 0, yLo + r),
    `V ${height - r}`,
    arc(r, r, 0, r, height),
    "Z",
  ].join(" ")
}

export function buildCardShapePaths(layout: CardShapeLayout) {
  const {
    innerW,
    bottomBlockH,
    topBottomLeft,
    topBottomRight,
    bottomTopLeftLocal,
    stepW,
    bannerRadius,
    innerRadius,
  } = layout

  return {
    hero: buildTopBannerPath(
      innerW,
      bannerRadius,
      innerRadius,
      topBottomLeft,
      topBottomRight,
      stepW
    ),
    footer: buildBottomBannerPath(
      innerW,
      bottomBlockH,
      bannerRadius,
      innerRadius,
      bottomTopLeftLocal,
      0,
      stepW,
      topBottomRight
    ),
  }
}
